import Drawer from 'antd/es/drawer';
import { ComponentPropsWithoutRef } from 'react';
import styled, { css } from 'styled-components';
import { rgba } from 'polished';

import { c080820, c333353, mainGridGapColor } from '#/colors';

import Success from '#/components/common/toast/Success';
import ToastTitle from '#/components/common/toast/Title';
import BaseContainer from '#/components/common/toast/BaseContainer';
import { APP_BAR_HEIGHT } from '#/components/common/app-bar/styles';
import { MARKET_INFO_AREA_HEIGHT } from '#/components/shared/styles';

import {
  useNotificationsDrawerActions,
  useNotificationsDrawerView,
} from '#/features/notifications/drawer-context';
import {
  useNotificationsActions,
  useNotificationsView,
} from '#/features/notifications/notifications-context';
import {
  NotificationEntryByType,
  NotificationEntryType,
} from '#/features/notifications/types';
import { NOTIFICATIONS_MAP } from '#/features/notifications/map';

import NotificationsDrawerSettingsMenu from './NotificationsDrawerSettingsMenu';
import NotificationsDrawerActions from './NotificationsDrawerActions';
import { NOTIFICATIONS_DRAWER_WIDTH } from './settings';

type DrawerProps = ComponentPropsWithoutRef<typeof Drawer>;

export default function NotificationsDrawer() {
  const drawerView = useNotificationsDrawerView();
  const drawerActions = useNotificationsDrawerActions();
  const notificationsView = useNotificationsView();
  const notificationsActions = useNotificationsActions();

  function getContents() {
    if (notificationsView.isEmpty) return <AllCaughtUp />;

    return notificationsView.notifications.map((notification) => (
      <Notification key={notification.id} entry={notification} />
    ));
  }

  function handleDrawerClose() {
    notificationsActions.markAllAsRead();
    drawerActions.close();
  }

  return (
    <StyledDrawer
      width={NOTIFICATIONS_DRAWER_WIDTH}
      title={<DrawerTitle />}
      placement="right"
      closable
      open={drawerView.isOpen}
      onClose={handleDrawerClose}
      extra={<NotificationsDrawerActions />}
      data-testid="notification-drawer"
    >
      {drawerView.isSettingsMenuOpen && <NotificationsDrawerSettingsMenu />}
      {getContents()}
    </StyledDrawer>
  );
}

function DrawerTitle() {
  return <span data-testid="notifications-center-title">Notifications</span>;
}

function AllCaughtUp() {
  return (
    <NotificationContainer>
      <Success>
        <ToastTitle>All caught up!</ToastTitle>
        You have no new notifications
      </Success>
    </NotificationContainer>
  );
}

function Notification<T extends NotificationEntryType>(props: {
  entry: NotificationEntryByType[T];
}) {
  const { entry } = props;
  const { Component } = NOTIFICATIONS_MAP[entry.kind];

  return (
    <NotificationContainer isSeen={entry.is_seen} key={entry.id}>
      <Component {...entry.props} />
    </NotificationContainer>
  );
}

const StyledDrawer = styled(({ className, children, ...rest }: DrawerProps) => (
  <Drawer rootClassName={className} {...rest}>
    {children}
  </Drawer>
))`
  color: inherit;
  font: inherit;

  .ant-drawer-mask {
    background: none;
  }

  .ant-drawer-header {
    min-height: ${MARKET_INFO_AREA_HEIGHT};
    padding: 16px;
    border-bottom: 1px solid ${mainGridGapColor};
  }

  .ant-drawer-close {
    display: none; /** only used during test automation */
  }

  .ant-drawer-title {
    color: inherit;
    font-size: 14px;
    font-weight: 700;
  }

  .ant-drawer-extra {
    height: 100%;
  }

  .ant-drawer-content-wrapper {
    margin-top: auto;
    height: calc(100% - ${APP_BAR_HEIGHT});
  }

  .ant-drawer-content {
    background: ${c080820};
    border-left: 1px solid ${mainGridGapColor};
  }

  .ant-drawer-body {
    padding: 0px;
  }
`;

const spacerBottomBorderCss = css`
  border-bottom: 1px solid;
  border-image-slice: 1;
  border-image-source: linear-gradient(
    to left,
    ${rgba(c333353, 0)},
    ${c333353},
    ${rgba(c333353, 0)}
  );
`;

const NotificationContainer = styled.div<{ isSeen?: boolean }>`
  min-height: 60px;
  padding: 16px;

  & ${BaseContainer} {
    background: transparent;
    backdrop-filter: none;
    border: none;
    padding: 0px;
    padding-right: 38px;

    ${({ isSeen }) => {
      if (isSeen == null) return;
      return (
        isSeen &&
        css`
          opacity: 0.5;
        `
      );
    }}
  }

  :not(:last-child) {
    ${spacerBottomBorderCss}
  }
`;
